import React, { useEffect, useState } from 'react';

/**
 * Mini animated Performance Card preview.
 * Cycles through Common → Rare → Legendary → Mythic every 2.4s with a soft flip.
 * Visual-only — does NOT use real portfolio data. Sits inside the home promo.
 */
const RARITIES = [
  {
    key: 'common',
    label: 'COMMON',
    tint: '#b87333',
    glow: 'rgba(184,115,51,0.55)',
    frame: 'linear-gradient(135deg, #b87333 0%, #e8a87c 50%, #b87333 100%)',
    emoji: '🌱',
    value: '$420',
    threshold: '< $500',
  },
  {
    key: 'rare',
    label: 'RARE',
    tint: '#00f4ff',
    glow: 'rgba(0,244,255,0.55)',
    frame: 'linear-gradient(135deg, #00f4ff 0%, #12b7ff 50%, #7c3aed 100%)',
    emoji: '💎',
    value: '$3,200',
    threshold: '< $5K',
  },
  {
    key: 'legendary',
    label: 'LEGENDARY',
    tint: '#ffd700',
    glow: 'rgba(255,215,0,0.6)',
    frame: 'linear-gradient(135deg, #ffd700 0%, #ff00aa 50%, #7c3aed 100%)',
    emoji: '🚀',
    value: '$18,500',
    threshold: '< $25K',
  },
  {
    key: 'mythic',
    label: 'MYTHIC',
    tint: '#ff00aa',
    glow: 'rgba(255,0,170,0.55)',
    frame: 'linear-gradient(135deg, #00f4ff 0%, #ff00aa 33%, #ffd700 66%, #7c3aed 100%)',
    emoji: '👑',
    value: '$42K',
    threshold: '≥ $25K',
  },
];

const PerformanceCardPreview = ({ width = 220 }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % RARITIES.length), 2400);
    return () => clearInterval(t);
  }, []);
  const r = RARITIES[idx];
  const h = Math.round(width * 1.4); // aspect ratio ≈ 5:7 like a trading card

  return (
    <div
      className="relative select-none"
      style={{ width, height: h, perspective: 1000 }}
      data-testid="perf-card-preview"
      aria-hidden="true"
    >
      <div
        key={r.key}
        className="absolute inset-0 rounded-2xl overflow-hidden"
        style={{
          background: r.frame,
          boxShadow: `0 25px 60px -15px ${r.glow}, 0 0 0 1px ${r.tint}44`,
          animation: 'sim360-card-flip 700ms cubic-bezier(.2,.8,.2,1)',
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Inner card */}
        <div
          className="absolute inset-[6px] rounded-[14px] p-3 flex flex-col"
          style={{
            background: 'linear-gradient(180deg, #040816 0%, #0b1c38 100%)',
          }}
        >
          {/* Holographic diagonal stripes */}
          <div
            className="absolute inset-0 opacity-30 mix-blend-screen pointer-events-none"
            style={{
              backgroundImage: `repeating-linear-gradient(115deg, ${r.tint}55 0px, transparent 6px, transparent 14px, ${r.tint}22 20px)`,
              animation: 'sim360-card-shimmer 6s linear infinite',
            }}
          />

          {/* Header */}
          <div className="flex items-center justify-between relative z-10">
            <div
              className="text-[8px] tracking-[0.35em] font-bold"
              style={{ color: r.tint, textShadow: `0 0 8px ${r.glow}` }}
            >
              PERF CARD
            </div>
            <div
              className="text-[8px] font-black"
              style={{ color: r.tint, textShadow: `0 0 8px ${r.glow}` }}
            >
              {r.label}
            </div>
          </div>

          {/* Emoji centerpiece */}
          <div className="flex-1 flex items-center justify-center relative z-10">
            <div
              style={{
                fontSize: width * 0.45,
                lineHeight: 1,
                filter: `drop-shadow(0 0 18px ${r.glow})`,
                animation: 'sim360-card-pulse 2.4s ease-in-out infinite',
              }}
            >
              {r.emoji}
            </div>
          </div>

          {/* Mock total */}
          <div className="relative z-10 text-center">
            <div className="text-[8px] tracking-[0.3em] text-[#9EB4C0]">TOTAL</div>
            <div
              className="font-black"
              style={{
                fontSize: width * 0.16,
                color: r.tint,
                textShadow: `0 0 14px ${r.glow}`,
                fontFamily: 'ui-monospace, monospace',
              }}
            >
              {r.value}
            </div>
            <div className="text-[8px] text-[#556475] mt-0.5">{r.threshold}</div>
          </div>

          {/* Bottom bar — fake serial */}
          <div className="relative z-10 mt-2 flex items-center justify-between text-[7px] text-[#556475] font-mono">
            <span>SIM360°</span>
            <span>#{String(1234 + idx * 7).padStart(5, '0')}</span>
          </div>
        </div>
      </div>

      {/* Rarity dots indicator */}
      <div className="absolute -bottom-4 left-0 right-0 flex items-center justify-center gap-1.5">
        {RARITIES.map((rr, i) => (
          <span
            key={rr.key}
            className="block w-1.5 h-1.5 rounded-full transition-all duration-500"
            style={{
              background: i === idx ? rr.tint : 'rgba(255,255,255,0.15)',
              boxShadow: i === idx ? `0 0 8px ${rr.glow}` : 'none',
              transform: i === idx ? 'scale(1.4)' : 'scale(1)',
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes sim360-card-flip {
          0%   { transform: rotateY(90deg) scale(0.9); opacity: 0; }
          60%  { transform: rotateY(-8deg) scale(1.02); opacity: 1; }
          100% { transform: rotateY(0deg) scale(1); opacity: 1; }
        }
        @keyframes sim360-card-shimmer {
          0%   { transform: translateX(-12%); }
          100% { transform: translateX(12%); }
        }
        @keyframes sim360-card-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.08); }
        }
      `}</style>
    </div>
  );
};

export default PerformanceCardPreview;
